import * as THREE from 'three'
import { ImportedModelResourceLease } from './ImportedModelResourceLease'

/**
 * 导入模型 Three.js 资源的统一所有权注册表。
 *
 * 注册表以对象引用而不是文件名、结果 kind 或 userData 判断共享关系。只有最后一个租约归还后
 * 才销毁对应资源，从而同时支持完整归档释放和归档内部 node 的局部替换。
 */
export class ImportedModelResourceOwnershipRegistry {
  /** Geometry 到活动租约数量。 */
  private readonly geometryOwnerCounts = new Map<THREE.BufferGeometry, number>()

  /** Material 到活动租约数量。 */
  private readonly materialOwnerCounts = new Map<THREE.Material, number>()

  /** Texture 到活动租约数量。 */
  private readonly textureOwnerCounts = new Map<THREE.Texture, number>()

  /** 注册表是否已经结束生命周期。 */
  private disposed = false

  /**
   * 为一组真实资源取得一次所有权。
   *
   * @param resources 调用方按 node/occurrence 收集的资源集合。
   * @returns 可独立 retain/release 的资源租约。
   */
  public acquire(resources: PuppyCAD.ImportedModelResourceSet): ImportedModelResourceLease {
    this.assertAvailable()
    const normalizedResources = this.normalizeResources(resources)
    this.incrementOwners(this.geometryOwnerCounts, normalizedResources.geometries)
    this.incrementOwners(this.materialOwnerCounts, normalizedResources.materials)
    this.incrementOwners(this.textureOwnerCounts, normalizedResources.textures)
    return new ImportedModelResourceLease(this, normalizedResources)
  }

  /**
   * 归还一个租约持有的资源。
   *
   * 该入口只供 `ImportedModelResourceLease` 调用。先完成全部引用递减，再按纹理、材质、
   * 几何的顺序销毁归零资源，避免材质销毁后遗漏纹理引用。
   * Object3D 根只用于描述 node 作用域，不在这里摘除或清空；场景树变更必须由发布事务显式完成。
   *
   * @param resources 当前句柄取得所有权时冻结的资源集合。
   */
  public release(resources: PuppyCAD.ImportedModelResourceSet): void {
    if (this.disposed) {
      return
    }
    const releasedGeometries = this.decrementOwners(this.geometryOwnerCounts, resources.geometries)
    const releasedMaterials = this.decrementOwners(this.materialOwnerCounts, resources.materials)
    const releasedTextures = this.decrementOwners(this.textureOwnerCounts, resources.textures)

    this.disposeReleasedResources(releasedTextures, releasedMaterials, releasedGeometries)
  }

  /**
   * 强制结束注册表生命周期并释放仍登记的全部资源。
   *
   * Editor 销毁时调用；所有资源按对象引用去重，因此每个真实资源最多销毁一次。
   */
  public dispose(): void {
    if (this.disposed) {
      return
    }
    this.disposed = true
    const geometries = [...this.geometryOwnerCounts.keys()]
    const materials = [...this.materialOwnerCounts.keys()]
    const textures = [...this.textureOwnerCounts.keys()]
    this.geometryOwnerCounts.clear()
    this.materialOwnerCounts.clear()
    this.textureOwnerCounts.clear()

    this.disposeReleasedResources(textures, materials, geometries)
  }

  /**
   * 复制调用方集合，防止租约创建后外部修改集合内容导致引用数失配。
   *
   * @param resources 原始资源集合。
   * @returns 当前租约独占的集合容器；集合内部仍是原始资源引用。
   */
  private normalizeResources(
    resources: PuppyCAD.ImportedModelResourceSet,
  ): PuppyCAD.ImportedModelResourceSet {
    return Object.freeze({
      objectRoots: new Set(resources.objectRoots),
      geometries: new Set(resources.geometries),
      materials: new Set(resources.materials),
      textures: new Set(resources.textures),
    })
  }

  /**
   * 为资源集合增加一次 owner。
   *
   * @param ownerCounts 资源引用计数表。
   * @param resources 当前租约持有的资源。
   */
  private incrementOwners<T>(ownerCounts: Map<T, number>, resources: Iterable<T>): void {
    for (const resource of resources) {
      ownerCounts.set(resource, (ownerCounts.get(resource) ?? 0) + 1)
    }
  }

  /**
   * 为资源集合减少一次 owner，并返回引用数已经归零的资源。
   *
   * @param ownerCounts 资源引用计数表。
   * @param resources 当前租约归还的资源。
   * @returns 本次归还后需要真正销毁的资源。
   */
  private decrementOwners<T>(ownerCounts: Map<T, number>, resources: Iterable<T>): T[] {
    const released: T[] = []
    for (const resource of resources) {
      const currentCount = ownerCounts.get(resource)
      if (currentCount === undefined) {
        continue
      }
      if (currentCount > 1) {
        ownerCounts.set(resource, currentCount - 1)
        continue
      }
      ownerCounts.delete(resource)
      released.push(resource)
    }
    return released
  }

  /**
   * 按纹理、材质、几何顺序释放资源，并保证单项异常不阻断其它资源清理。
   *
   * @param textures 已归零纹理。
   * @param materials 已归零材质。
   * @param geometries 已归零几何。
   */
  private disposeReleasedResources(
    textures: readonly THREE.Texture[],
    materials: readonly THREE.Material[],
    geometries: readonly THREE.BufferGeometry[],
  ): void {
    const errors: unknown[] = []
    ;[...textures, ...materials, ...geometries].forEach((resource) => {
      try {
        resource.dispose()
      } catch (error) {
        errors.push(error)
      }
    })
    if (errors.length > 0) {
      throw new AggregateError(errors, '导入模型 GPU 资源释放失败')
    }
  }

  /** 注册表销毁后禁止创建新的资源 owner。 */
  private assertAvailable(): void {
    if (this.disposed) {
      throw new Error('导入模型资源所有权注册表已销毁')
    }
  }
}
